import { DomainForm } from "@/components/domain";
import { DraggableList } from "@/components/draggable";
import { SelectSearch } from "@/components/select-search";
import { Button } from "@/components/ui/button";
import { Card, CardHeader } from "@/components/ui/card";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import { useGetAllHotelRooms, useGetHotelRoom } from "@/hooks/hotel-rooms";
import { T } from "@/i18n";
import { CreateTourAccommodation, TourAccommodation } from "@/models";
import { useForm } from "@deadcow-enterprises/react-forms";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Pencil, Plus, Trash } from "lucide-react";
import { useState } from "react";
import { z } from "zod";
import { useTourDaysContext } from "./context";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";

export function Accommodations({
  day,
  accommodations,
  tourId,
}: {
  accommodations: TourAccommodation[];
  day: number;
  tourId: number;
}) {
  const { reorderAccommodations, addNewAccommodation } = useTourDaysContext();
  return (
    <div className="grid gap-4">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-semibold">
          <T tKey="tourDays.accommodation.title" />
        </span>
        <UpsertCard
          day={day}
          priority={accommodations.length + 1}
          tourId={tourId}
          onUpsert={async (model) => {
            addNewAccommodation(day, model);
          }}
        >
          <Button size="sm" variant="text" className="m-1">
            <Plus />
            <T tKey="tourDays.accommodation.new" />
          </Button>
        </UpsertCard>
      </div>
      <div className="grid gap-2">
        <DraggableList
          list={accommodations}
          getId={(s) => s.id}
          onChange={(v) =>
            reorderAccommodations(
              day,
              v.map((s) => s.priority)
            )
          }
          render={(elem, i) => (
            <AccommodationCard
              accommodation={elem}
              key={elem.id}
              mainAccommodation={i === 0}
            />
          )}
        ></DraggableList>
      </div>
    </div>
  );
}

function AccommodationCard({
  accommodation,
  mainAccommodation,
}: {
  accommodation: TourAccommodation;
  mainAccommodation: boolean;
}) {
  const { data: room, isLoading } = useGetHotelRoom(
    accommodation.hotelRoomId?.toString() ?? ""
  );
  const { updateAccommodation, deleteAccommodation } = useTourDaysContext();
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: accommodation.id });

  return (
    <div
      className="flex gap-2 items-center"
      ref={setNodeRef}
      style={{
        transition,
        transform: CSS.Transform.toString(transform),
      }}
    >
      <Button
        type="button"
        className="cursor-grab"
        variant="ghost"
        {...attributes}
        {...listeners}
      >
        <GripVertical />
      </Button>
      <Card
        className={cn(
          "w-full relative isolate",
          mainAccommodation && "border-primary"
        )}
      >
        <CardHeader className="flex-row space-y-0 gap-2 justify-between items-start">
          <div className="grid gap-1 min-w-[20%]">
            {isLoading || !room ? (
              <>
                <Skeleton className="w-full h-5" />
                <Skeleton className="w-full h-3" />
              </>
            ) : (
              <>
                <span className="font-semibold">{room.hotelName ?? "-"}</span>
                <span className="text-primary">{room.designation ?? "-"}</span>
              </>
            )}
          </div>
          <div>
            <UpsertCard
              day={accommodation.day}
              priority={accommodation.priority}
              tourId={accommodation.tourId}
              model={accommodation}
              onUpsert={async (model) => {
                updateAccommodation(accommodation.day, {
                  id: accommodation.id,
                  ...model,
                });
              }}
            >
              <Button variant="ghost" size="icon" className="m-1">
                <Pencil />
                <span className="sr-only">
                  <T tKey="tourDays.accommodation.edit" />
                </span>
              </Button>
            </UpsertCard>
            <Button
              variant="text-destructive"
              size="icon"
              className="m-1"
              onClick={() =>
                deleteAccommodation(accommodation.day, accommodation.id)
              }
            >
              <Trash />
              <span className="sr-only">
                <T tKey="tourDays.accommodation.delete" />
              </span>
            </Button>
          </div>
        </CardHeader>
        {mainAccommodation ? (
          <Badge className="font-bold absolute top-0 -translate-y-1/2 left-4">
            <T tKey="tourDays.accommodation.main" />
          </Badge>
        ) : (
          <></>
        )}
      </Card>
    </div>
  );
}

function UpsertCard({
  day,
  priority,
  tourId,
  onUpsert,
  model,
  children,
}: {
  day: number;
  tourId: number;
  priority: number;
  model?: CreateTourAccommodation;
  onUpsert: (v: CreateTourAccommodation) => Promise<void>;
  children?: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent className="w-80" align="end">
        <Form
          model={model}
          onUpsert={async (v) => {
            await onUpsert(v);
            if (!model) setOpen(false);
          }}
          day={day}
          priority={priority}
          tourId={tourId}
        />
      </PopoverContent>
    </Popover>
  );
}

function Form({
  day,
  priority,
  tourId,
  onUpsert,
  model,
}: {
  day: number;
  tourId: number;
  priority: number;
  model?: CreateTourAccommodation;
  onUpsert: (v: CreateTourAccommodation) => Promise<void>;
}) {
  const { data: rooms, isLoading: roomsLoading } = useGetAllHotelRooms();
  const form = useForm<CreateTourAccommodation>({
    tourId: {
      value: model?.tourId ?? tourId,
    },
    notes: {
      value: model?.notes,
    },
    hotelRoomId: {
      value: model?.hotelRoomId ?? -1,
      validator: z.number().min(0, "errors.required"),
    },
    day: {
      value: model?.day ?? day,
    },
    priority: {
      value: model?.priority ?? priority,
    },
  });

  return (
    <DomainForm
      form={form}
      onUpsert={onUpsert}
      buttonTranslation={
        !model
          ? "tourDays.accommodation.create"
          : "tourDays.accommodation.update"
      }
      fields={[
        {
          key: "hotelRoomId",
          label: "tourDays.accommodation.fields.hotelRoomId",
          input: (form, id) =>
            roomsLoading || !rooms ? (
              <Skeleton className="w-full h-9" />
            ) : (
              <SelectSearch
                id={id}
                className="w-full"
                options={rooms.map((room) => ({
                  label: `${room.hotelName ?? ""} - ${room.designation ?? ""}`,
                  value: Number.parseInt(room.id),
                }))}
                value={form.value.hotelRoomId}
                onChange={(value) => {
                  form.setValue("hotelRoomId", value);
                }}
              />
            ),
        },
      ]}
    />
  );
}
